import { useMemo, useState } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Helmet } from 'react-helmet-async'

const navigation = [
  { name: 'Kronos köpskydd', href: '/kronos-secure' },
  { name: 'Kronos Certified', href: '/kronos-certified' },
  { name: 'Kontrollera certifiering', href: '/kronos-certified-control' },
  { name: 'Klockvärdering', href: '/klockvardering' },
  { name: 'Företagsannonsering', href: '/foretagsannonsering' },
  { name: 'Marknadsföring', href: '/marknadsforing' },
  { name: 'Blogg', href: '/blogg' },
  { name: 'Integritetspolicy', href: '/integritetspolicy' },
  { name: 'Användarvillkor', href: '/anvandarvillkor' },
]

const brands = [
  {
    name: 'Tissot',
    image: 'https://www.stjarnurmakarna.se/pub_images/original/Tissot-PRX-T1374071109100.jpg',

    category: 'Mellansegment',

    description:
      'Schweiziskt urmakeri som kombinerar tradition, modern design och prisvärd kvalitet.',

    models: ['PRX', 'Seastar', 'Le Locle'],
  },

  {
    name: 'Seiko',
    image:
      'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRSmeWxC8J876kZndLTkBlWnyRXmm-tVStThPJv6nQcfu_PmJWwivCqy8sC&s=10',

    category: 'Mellansegment',

    description:
      'Japansk klocktillverkare med allt från klassiska vardagsklockor till avancerade dykarklockor.',

    models: ['Prospex', 'Presage', '5 Sports'],
    link: '/seiko',
  },

  {
    name: 'Certina',
    image:
      'https://monochrome-watches.com/app/uploads/2025/11/2025-updated-Certina-DS-Action-Diver-38mm-Powermatic-80-collection-review-8.jpg',

    category: 'Mellansegment',

    description: 'Sportiga klockor med fokus på tålighet, funktion och schweizisk kvalitet.',

    models: ['DS Action', 'DS-8', 'DS PH200M'],
    link: '/certina',
  },

  {
    name: 'Longines',
    image: 'https://img.chrono24.com/images/uhren/47914739-gqvgvc5ddc1r4lnhknluds6h-ExtraLarge.jpg',

    category: 'Mellansegment',

    description: 'Klassisk schweizisk elegans med ett rikt arv och modeller för alla tillfällen.',

    models: ['HydroConquest', 'Master Collection', 'Spirit'],
    link: '/longines',
  },

  {
    name: 'Hamilton',
    image:
      'https://www.horobox.com/Cms_Data/Contents/SampleBusiness/Media/Incelemeler/Hamilton-Khaki-Field-Mechanical-38-mm-Yeni-Modeller/hamilton-khaki-field-mechanical-38-mm-yeni-modeller-3.jpg',

    category: 'Mellansegment',

    description:
      'Amerikanskt designarv möter schweizisk tillverkning i klockor med tydlig karaktär.',

    models: ['Khaki Field', 'Jazzmaster', 'Ventura'],
  },

  {
    name: 'Oris',
    image: 'https://www.klockmaster.se/static/files/8/73377894154mb-1.jpg',

    category: 'Mellansegment',

    description:
      'Självständig schweizisk tillverkare med fokus på mekaniska urverk och distinkt design.',

    models: ['Aquis', 'Divers Sixty Five', 'Big Crown'],
  },

  {
    name: 'Frédérique Constant',
    image:
      'https://oracleoftime.com/wp-content/uploads/2026/09/Frederique-Constant-Classics-Moneta-Handwinding-3.jpg',

    category: 'Mellansegment',

    description: 'Klassiskt schweiziskt urmakeri med elegant formgivning och mekanisk tradition.',

    models: ['Classics', 'Highlife', 'Slimline'],
  },

  {
    name: 'Mido',
    image:
      'https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEhPUO2tIEyqKEM_d3IwhtDpgD-YgAWQ1TYbbW_1X-GWcVF7cUK3Z8TKo5e-8rYf9nTn8PMiWPEI726qgJoE1AlyP1vhzpfMMK52eEeBFdBXd_CDDJInkP2W6wci7PDIWRJb8NSNqmoSwR-XsksLBNeDxGjJ-YD3TRprrAyxRKfN4K7XyUKYQgi9PaTTm1I/s1600/Mido%20Ocean%20Star%20200%20M026.930.11.011.00_005.jpg',

    category: 'Mellansegment',

    description:
      'Schweiziska klockor som förenar tidlös design, arkitektonisk inspiration och precision.',

    models: ['Ocean Star', 'Multifort', 'Commander'],
  },

  {
    name: 'Rado',
    image: 'https://cdn.shopify.com/s/files/1/0609/1636/7523/files/RADO-Nato-1200.jpg',

    category: 'Mellansegment',

    description: 'Känt för innovativa material, reptålig keramik och modern schweizisk design.',

    models: ['Captain Cook', 'DiaStar', 'True'],
  },

  {
    name: 'TAG Heuer',
    image:
      'https://media.gq-magazin.de/photos/64afb12d5c95a13af88a0c26/16:9/w_2560%2Cc_limit/CBS2213.FN6002_HERO2_RGB.jpg',

    category: 'Mellansegment',

    description:
      'Sportig schweizisk klockdesign med starka kopplingar till motorsport och tidtagning.',

    models: ['Carrera', 'Monaco', 'Aquaracer'],
  },

  {
    name: 'Omega',
    image:
      'https://swisswatches-magazine.com/uploads/2024/11/omega-diver-300m-210-30-42-20-06-002-dial.webp',

    category: 'Premium och lyx',

    description:
      'Ett ikoniskt schweiziskt märke med historik inom rymdfart, sport och precisionsur.',

    models: ['Speedmaster', 'Seamaster', 'Constellation'],
    link: '/omega',
  },

  {
    name: 'Rolex',
    image:
      'https://www.watchclub.com/upload/watches/gallery_big/watch-club-rolex-gmt-master-ii-126710blro-pepsi-ref-126710blro-year-2020-1752167704253-16122-1.jpg',

    category: 'Premium och lyx',

    description:
      'Ett av världens mest välkända klockmärken, uppskattat för ikonisk design och robust kvalitet.',

    models: ['Submariner', 'Daytona', 'GMT Master II'],
    link: '/rolex',
  },

  {
    name: 'Breitling',
    image:
      'https://www.theluxuryhut.com/_next/image/?url=https%3A%2F%2Fassets.theluxuryhut.com%2Fcms%2Fadmin%2Fupload%2F2026%2F08%2F2f960d18-cbbd-4f7b-88fd-dfe911d4fc1dbanner.jpg&w=1080&q=75',

    category: 'Premium och lyx',

    description: 'Tekniska klockor med en lång historia inom flyg, kronografer och sportig design.',

    models: ['Navitimer', 'Superocean', 'Chronomat'],
    link: '/breitling',
  },

  {
    name: 'IWC Schaffhausen',
    image:
      'https://swisswatches-magazine.com/uploads/2024/12/iwc-ingenieur-automatic-40-blue-iw328902-dial.webp',

    category: 'Premium och lyx',

    description:
      'Schweiziskt ingenjörskunnande med en stark identitet inom pilotklockor och mekanik.',

    models: ['Portugieser', 'Big Pilot', 'Ingenieur'],
    link: '/iwc',
  },

  {
    name: 'Panerai',
    image:
      'https://b2962381.assetcdn.net/2.0/2962381/wp-content/uploads/2025/04/panerai-2025_0016_PANERAI_EVOLUTION_01_D-1600x1333.jpg?lossy=0&strip=1&webp=1',

    category: 'Premium och lyx',

    description:
      'Klockor med italiensk designtradition, kraftfulla former och lättigenkännliga urtavlor.',

    models: ['Luminor', 'Radiomir', 'Submersible'],
    link: '/panerai',
  },

  {
    name: 'Cartier',
    image:
      'https://grandcaliber.com/cdn/shop/files/CartierWatches-PDP-Banner-Image.jpg?v=1782395109&width=3000',

    category: 'Premium och lyx',

    description: 'Ett ikoniskt designhus där smyckeskonst möter exklusiv klocktillverkning.',

    models: ['Tank', 'Santos', 'Ballon Bleu'],
    link: '/cartier',
  },

  {
    name: 'Jaeger LeCoultre',
    image:
      'https://www.coveted.com/_next/image?url=https%3A%2F%2Fassets.coveted.com%2Fwatches%2Fimages%2Fjaeger_lecoultre%2Fca05c53b8e_d3b8993b30854435ccdb75ef33fd07bc56a8642b.jpg&w=3840&q=60',

    category: 'Premium och lyx',

    description:
      'Respekterat schweiziskt manufakturhus med lång tradition av teknisk innovation och avancerade urverk.',

    models: ['Reverso', 'Master Control', 'Polaris'],
    link: '/jaeger-lecoultre',
  },

  {
    name: 'Zenith',
    image:
      'https://zenith-watches.bynder.com/asset/46ad92a0-a257-4dfd-80b9-443ce474bcff/Digital-RGB/1-SOLDAT-2_CHRONOMASTER-Original_03-3200-3600-52-C910_4x5.png?im=Resize=(200,200),aspect=fill;Crop=(0,0,200,200),gravity=Center;Blur=10&q=1',

    category: 'Premium och lyx',

    description:
      'Schweizisk tillverkare med en lång historia inom högprecision och mekaniska kronografer.',

    models: ['El Primero', 'Defy', 'Pilot'],
    link: '/zenith',
  },

  {
    name: 'Patek Philippe',
    image:
      'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTqNd72r_YGa7XcYb6Z01OiB7x9Oaxckz0qr0hrAtRtTw&s=10',

    category: 'Premium och lyx',

    description:
      'Ett av de mest ansedda namnen inom exklusivt schweiziskt urmakeri och komplicerade urverk.',

    models: ['Nautilus', 'Calatrava', 'Aquanaut'],
    link: '/patek-philippe',
  },

  {
    name: 'Audemars Piguet',
    image:
      'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRgQjrkkP3UqZcDGrcRGTu3x2bJJez6FTBunUV64RQwCQ&s=10',

    category: 'Premium och lyx',

    description:
      'Exklusivt schweiziskt urmakeri med djärv design och en central plats inom lyxiga sportklockor.',

    models: ['Royal Oak', 'Royal Oak Offshore', 'Code 11.59'],
    link: '/audemars-piguet',
  },
]

const categories = ['Alla märken', 'Mellansegment', 'Premium och lyx']

// Reservbilder (Unsplash) som visas om en märkesbild i /images/watch-brands saknas,
// så att varje märke alltid har en bild. Byt gärna ut mot riktiga märkesbilder.
const unsplash = (id) => `https://images.unsplash.com/${id}?q=80&w=800&auto=format&fit=crop`

const ROLEX_FALLBACK = unsplash('photo-1547996160-81dfa63595aa')

const FALLBACK_IMAGES = [
  unsplash('photo-1670177257750-9b47927f68eb'),
  unsplash('photo-1670404160620-a3a86428560e'),
  unsplash('photo-1600003014637-ff82a275e191'),
  unsplash('photo-1600003014608-c2ccc1570a65'),
  unsplash('photo-1620625515032-6ed0c1790c75'),
  unsplash('photo-1524805444758-089113d48a6d'),
  unsplash('photo-1587925358603-c2eea5305bbc'),
  unsplash('photo-1634140704051-58a787556cd1'),
  unsplash('photo-1636639818651-d97365346a5c'),
  unsplash('photo-1649357585015-179ed98f513d'),
  unsplash('photo-1634595947394-87012e7b12ba'),
  unsplash('photo-1691865179028-1729b766a5cd'),
  unsplash('photo-1600003014755-ba31aa59c4b6'),
]

function getFallbackImage(brand, index) {
  if (brand.name === 'Rolex') return ROLEX_FALLBACK
  return FALLBACK_IMAGES[(index - 1) % FALLBACK_IMAGES.length]
}

export default function WatchBrands({ siteLogo }) {
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState('Alla märken')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const filteredBrands = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('sv-SE')

    return brands.filter((brand) => {
      const matchesCategory = activeCategory === 'Alla märken' || brand.category === activeCategory

      const searchableText = [brand.name, brand.category, brand.description, ...brand.models]
        .join(' ')
        .toLocaleLowerCase('sv-SE')

      return matchesCategory && searchableText.includes(query)
    })
  }, [search, activeCategory])

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Klockmärken på Kronos',
    description: 'Utforska populära klockmärken och deras välkända modeller på Kronos.',
    numberOfItems: brands.length,
    itemListElement: brands.map((brand, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Brand',
        name: brand.name,
        description: brand.description,
        url: `https://www.kronos.se/klockmarken#${brand.name
          .toLocaleLowerCase('sv-SE')
          .replace(/[^a-z0-9]+/g, '-')}`,
      },
    })),
  }

  return (
    <>
      <Helmet>
        <title>Klockmärken: 20 populära märken från Tissot till Rolex | Kronos</title>
        <meta
          name="description"
          content="Utforska 20 klockmärken och deras populära modeller, från prisvärt mellansegment till premium och lyx. Hitta din nästa klocka på Kronos."
        />
        <link rel="canonical" href="https://www.kronos.se/klockmarken" />
      </Helmet>

      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      <main className="min-h-screen bg-[#f4f3f0] text-black font-poppins">
        {/* HEADER */}
        <header className="fixed inset-x-0 top-0 z-50 bg-white shadow">
          <nav className="flex items-center justify-between p-6 lg:px-8">
            <a href="/" className="flex items-center -m-1.5 p-1.5">
              <span className="sr-only">Krono</span>
              <img alt="Krono Logo" src={siteLogo} className="h-6 w-auto rounded-md" />
            </a>

            {/* Mobile menu button */}
            <div className="flex lg:hidden">
              <button
                type="button"
                className="p-2.5 text-gray-700 rounded-md"
                onClick={() => setMobileMenuOpen(true)}
              >
                <span className="sr-only">Open menu</span>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
              </button>
            </div>

            {/* Desktop navigation */}
            <div className="hidden lg:flex lg:gap-x-8">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-sm font-semibold text-gray-900 hover:underline"
                >
                  {item.name}
                </a>
              ))}
            </div>
          </nav>

          {/* Mobile menu */}
          <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
            <div className="fixed inset-0 z-50" />
            <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white px-6 py-6 overflow-y-auto shadow-lg">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2.5 text-gray-700 rounded-md"
                >
                  <span className="sr-only">Close menu</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="mt-6 space-y-2">
                {navigation.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    {item.name}
                  </a>
                ))}
              </div>
            </DialogPanel>
          </Dialog>
        </header>

        {/* HERO */}
        <section
          className="relative flex min-h-[540px] items-center justify-center overflow-hidden px-6 pb-24 pt-32 text-center md:min-h-[620px]"
          style={{
            backgroundColor: '#e6e5e1',
            backgroundImage: [
              'radial-gradient(ellipse at 8% 100%, rgba(190,190,185,.55), transparent 45%)',
              'radial-gradient(ellipse at 40% 15%, rgba(242,241,238,.9), transparent 55%)',
              'linear-gradient(90deg, #dcdcd7 0%, #e7e6e2 40%, #e3e3df 70%, #dedfda 100%)',
            ].join(', '),
          }}
        >
          <div className="relative z-10 mx-auto max-w-5xl">
            <span className="mb-7 inline-flex rounded-full bg-white/70 px-5 py-2 text-xs font-extrabold tracking-[0.22em] backdrop-blur">
              KRONOS · KLOCKGUIDE
            </span>

            <h1 className="mb-7 text-xl font-black leading-[1.05] tracking-tight md:text-7xl lg:text-8xl ">
              20 klockmärken att utforska, från prisvärt till lyx.
            </h1>

            <p className="mx-auto max-w-2xl text-base leading-relaxed text-gray-700 md:text-lg">
              Det rätta klockmärket beror på din budget, din stil och hur du tänker använda klockan.
              Vi har samlat 20 märken som ofta efterfrågas, fördelade på prisvärt mellansegment och
              premium och lyx. Använd sökfältet och filtren för att hitta märken och modeller som
              passar just dig.
            </p>

            <a
              href="#brand-list"
              className="mt-9 inline-flex items-center gap-4 rounded-full bg-black px-7 py-4 text-sm font-semibold text-white transition hover:bg-gray-800"
            >
              Utforska alla märken <span aria-hidden="true">↓</span>
            </a>

            <div className="mx-auto mt-16 flex max-w-2xl justify-between gap-4 border-t border-black/10 pt-5 text-left text-xs font-medium uppercase tracking-[0.14em] text-gray-600">
              <span>20 utvalda klockmärken</span>
              <span>En värld av klockor</span>
            </div>
          </div>
        </section>

        {/* BRAND LIST */}
        <section id="brand-list" className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <div className="mb-9 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.25em] text-gray-500">
                Upptäck klockvärlden
              </p>
              <h2 className="text-4xl font-black tracking-tight md:text-5xl">Utforska märken</h2>
            </div>
            <p className="text-sm text-gray-500">
              Visar {filteredBrands.length} av {brands.length} märken
            </p>
          </div>

          <div className="mb-5 flex items-center gap-3 rounded-2xl border border-black/10 bg-white px-5 shadow-sm focus-within:border-black/30">
            <svg
              className="h-5 w-5 shrink-0 text-gray-500"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="10.8" cy="10.8" r="6.8" stroke="currentColor" strokeWidth="1.7" />
              <path
                d="m16 16 4.2 4.2"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              />
            </svg>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Sök märke eller modell..."
              aria-label="Sök klockmärke eller modell"
              className="h-14 w-full bg-transparent text-sm outline-none placeholder:text-gray-400"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="shrink-0 text-xs font-semibold text-gray-500 hover:text-black"
              >
                Rensa
              </button>
            )}
          </div>

          <div className="mb-14 flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                aria-pressed={activeCategory === category}
                className={`rounded-full border px-5 py-3 text-xs font-semibold transition ${
                  activeCategory === category
                    ? 'border-black bg-black text-white'
                    : 'border-black/10 bg-white/70 text-gray-600 hover:border-black/30 hover:text-black'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {categories.slice(1).map((category) => {
            const categoryBrands = filteredBrands.filter((brand) => brand.category === category)
            if (!categoryBrands.length) return null

            return (
              <section key={category} className="mb-16 last:mb-0">
                <div className="mb-7 flex items-center gap-4 border-b border-black/10 pb-5">
                  <h3 className="text-2xl font-bold tracking-tight md:text-3xl">{category}</h3>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-500">
                    {String(categoryBrands.length).padStart(2, '0')}
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {categoryBrands.map((brand) => {
                    const index = brands.findIndex((item) => item.name === brand.name) + 1

                    return (
                      <a href={brand.link} rel="noopener noreferrer">
                        <article
                          id={brand.name.toLocaleLowerCase('sv-SE').replace(/[^a-z0-9]+/g, '-')}
                          key={brand.name}
                          className="group flex min-w-0 flex-col overflow-hidden rounded-[28px] bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
                        >
                          <div className="relative h-56 overflow-hidden bg-[#e8e5df] sm:h-64">
                            <img
                              src={brand.image}
                              alt={`${brand.name} klocka, exempel på ${brand.models[0] || brand.name}`}
                              width="800"
                              height="640"
                              loading="lazy"
                              decoding="async"
                              className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                              onError={(event) => {
                                const fallback = getFallbackImage(brand, index)
                                if (event.currentTarget.src !== fallback) {
                                  event.currentTarget.src = fallback
                                }
                              }}
                            />
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/65 via-black/5 to-transparent" />
                            <span className="absolute left-5 top-5 rounded-full bg-white/85 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-700 backdrop-blur">
                              {String(index).padStart(2, '0')} ·{' '}
                              {brand.category === 'Premium och lyx' ? 'Premium' : 'Utvalt märke'}
                            </span>
                            <h4 className="absolute bottom-5 left-6 right-5 break-words text-3xl font-black tracking-tight text-white drop-shadow sm:text-4xl">
                              {brand.name}
                            </h4>
                          </div>

                          <div className="flex flex-1 flex-col p-6 sm:p-7">
                            <p className="mb-7 text-sm leading-7 text-gray-600">
                              {brand.description}
                            </p>

                            <div className="mt-auto border-t border-black/5 pt-5">
                              <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">
                                Populära modeller
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {brand.models.map((model) => (
                                  <span
                                    key={model}
                                    className="rounded-full border border-black/5 bg-[#f4f3f0] px-3 py-2 text-[11px] font-medium text-gray-700"
                                  >
                                    {model}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </article>
                      </a>
                    )
                  })}
                </div>
              </section>
            )
          })}

          {filteredBrands.length === 0 && (
            <div className="rounded-[28px] bg-white px-6 py-16 text-center shadow-sm">
              <h3 className="mb-3 text-2xl font-bold">Inga märken hittades</h3>
              <p className="mb-6 text-sm text-gray-500">
                Prova ett annat sökord eller visa alla klockmärken.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearch('')
                  setActiveCategory('Alla märken')
                }}
                className="rounded-full bg-black px-6 py-3 text-sm font-semibold text-white transition hover:bg-gray-800"
              >
                Visa alla märken
              </button>
            </div>
          )}
        </section>

        {/* BOTTOM CTA */}
        <section className="bg-white px-6 py-20 text-center md:py-28">
          <p className="mb-5 text-xs font-extrabold uppercase tracking-[0.25em] text-gray-500">
            Din nästa klocka väntar
          </p>
          <h2 className="mx-auto mb-7 max-w-4xl text-4xl font-black leading-tight tracking-tight md:text-6xl">
            Upptäck klockor.
            <br />
            Hitta din favorit.
          </h2>
          <p className="mx-auto mb-8 max-w-xl text-sm leading-7 text-gray-600 md:text-base">
            Utforska klockor från privata säljare och professionella handlare på Kronos.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-4 rounded-full bg-black px-7 py-4 text-sm font-semibold text-white transition hover:bg-gray-800"
          >
            Upptäck Kronos <span aria-hidden="true">↗</span>
          </a>
        </section>
      </main>
    </>
  )
}
